import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  startSocialListeningBrandAnalysis,
  startSocialListeningCompetitiveAnalysis,
  SocialListeningCollectCompany,
} from "src/services/SocialListeningAPIs";
import "./loader.scss";

interface SocialListeningFormBrandRow {
  brandName?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  redditSubreddits?: string;
}

interface LoaderLocationState {
  competitors?: SocialListeningFormBrandRow[];
  brand?: SocialListeningFormBrandRow;
  analysisType?: "brand_analysis" | "competitive_analysis";
}

const normalizeHandle = (value?: string) => (value || "").trim().replace(/^@+/, "");

const normalizeFacebookPageId = (value?: string) => {
  const input = normalizeHandle(value);
  if (!input) {return "";}

  const withoutProtocol = input.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  const facebookHostMatch = withoutProtocol.match(/^(?:m\.|web\.)?facebook\.com\/(.+)$/i);
  const pagePath = facebookHostMatch?.[1] || withoutProtocol;
  const cleanPath = pagePath.split(/[?#]/)[0].replace(/^pages\//i, "");
  return cleanPath.split("/").filter(Boolean).pop() || "";
};

const parseSubreddits = (value?: string) =>
  (value || "")
    .split(/[\n,]+/)
    .map(item =>
      item
        .trim()
        .replace(/^https?:\/\/(?:www\.)?reddit\.com\/r\//i, "")
        .replace(/^\/?r\//i, "")
        .replace(/^@+/, "")
        .split(/[/?#]/)[0]
    )
    .filter(Boolean);

const formRowToCompany = (row: SocialListeningFormBrandRow): SocialListeningCollectCompany => {
  const company_name = (row.brandName || "").trim();
  const company: SocialListeningCollectCompany = { company_name };

  const facebook_handle = normalizeFacebookPageId(row.facebook);
  const instagram_handle = normalizeHandle(row.instagram);
  const tiktok_brand = normalizeHandle(row.tiktok);
  const reddit_additional_subreddits = parseSubreddits(row.redditSubreddits);

  if (facebook_handle) {company.facebook_handle = facebook_handle;}
  if (instagram_handle) {company.instagram_handle = instagram_handle;}
  if (tiktok_brand) {company.tiktok_brand = tiktok_brand;}
  if (reddit_additional_subreddits.length > 0) {
    company.reddit_additional_subreddits = reddit_additional_subreddits;
  }

  return company;
};

const SocialListingLoader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LoaderLocationState;
  const hasStartedRef = useRef(false);

  const analysisType = useMemo(() => {
    if (state.analysisType) {return state.analysisType;}
    if (state.competitors?.length) {return "competitive_analysis";}
    if (state.brand) {return "brand_analysis";}
    return null;
  }, [state.analysisType, state.brand, state.competitors]);

  useEffect(() => {
    if (hasStartedRef.current) {return;}
    const userIdRaw = localStorage.getItem("user_id");
    const tenantIdRaw = localStorage.getItem("tenant_id");
    const userId = Number(userIdRaw);
    const tenantId = Number(tenantIdRaw);

    const shouldGoToCompetitive =
      analysisType === "competitive_analysis" || Boolean(state.competitors?.length);
    const shouldGoToBrand =
      analysisType === "brand_analysis" || Boolean(state.brand);

    const competitors = state.competitors ?? [];
    const isCompetitiveValid =
      competitors.length >= 1 &&
      competitors.length <= 5 &&
      competitors.every(c => Boolean(c.brandName?.trim())) &&
      !Number.isNaN(userId) &&
      !Number.isNaN(tenantId) &&
      userIdRaw !== null &&
      tenantIdRaw !== null;

    const isBrandValid =
      Boolean(state.brand?.brandName?.trim()) &&
      !Number.isNaN(userId) &&
      !Number.isNaN(tenantId) &&
      userIdRaw !== null &&
      tenantIdRaw !== null;

    if (shouldGoToCompetitive && !isCompetitiveValid) {
      navigate("/account/social-listing-agent/competitive-analysis", {
        replace: true,
      });
      return;
    }

    if (shouldGoToBrand && !isBrandValid) {
      navigate("/account/social-listing-agent/brand-analysis", {
        replace: true,
      });
      return;
    }

    if (!analysisType) {
      navigate("/account/social-listing-agent", { replace: true });
      return;
    }

    hasStartedRef.current = true;

    if (shouldGoToCompetitive) {
      const companies: SocialListeningCollectCompany[] = competitors.map(formRowToCompany);
      const payload = { user_id: userId, tenant_id: tenantId, companies };
      startSocialListeningCompetitiveAnalysis(payload).catch(error => {
        // eslint-disable-next-line no-console -- surface API failures in dev console
        console.error("Social listening request failed:", error);
      });
    } else {
      const company = formRowToCompany(state.brand as SocialListeningFormBrandRow);
      startSocialListeningBrandAnalysis({
        user_id: userId,
        tenant_id: tenantId,
        ...company,
      }).catch(error => {
        // eslint-disable-next-line no-console -- surface API failures in dev console
        console.error("Social listening request failed:", error);
      });
    }
  }, [analysisType, navigate, state.brand, state.competitors]);

  const subtitle =
    analysisType === "competitive_analysis"
      ? "Analyzing competitors and gathering insights... Good things are coming."
      : "Analyzing your brand and gathering insights... Good things are coming.";

  return (
    <section className="social-listing-loader">
      <div className="social-listing-loader__card">
        <h1 className="social-listing-loader__title">
          Hang tight! We're on it
          <span className="social-listing-loader__spinner" aria-hidden="true" />
        </h1>
        <p className="social-listing-loader__subtitle">
          {subtitle}
        </p>
        <p className="social-listing-loader__note">
          We'll email you as soon as your report is ready.
        </p>

        <img
          className="social-listing-loader__animation"
          src="/Icons/Business chart.gif"
          alt="Report analysis in progress"
        />

        <button
          type="button"
          className="button button--primary social-listing-loader__button"
          onClick={() => navigate("/account/social-listing-agent/history")}
        >
          View all Reports
        </button>
      </div>
    </section>
  );
};

export default SocialListingLoader;
